import type { UnifiedCandidate } from '@/hooks/use-smart-search'

/** Result tier for analysis candidates — mirrors backend top/compelling/precluded. */
export type SmartAnalysisTier = 'top' | 'compelling' | 'precluded'

export const SMART_ANALYSIS_TIER_ORDER: SmartAnalysisTier[] = ['top', 'compelling', 'precluded']

export const SMART_ANALYSIS_TIER_LABELS: Record<SmartAnalysisTier, string> = {
  top: 'Strong matches',
  compelling: 'Good matches',
  precluded: 'Not recommended',
}

// Fallback thresholds when the API did not send a category (older sessions,
// live partial payloads). Mirror api/apps/search/categorization.py.
const TOP_FINAL_SCORE = 0.65
const COMPELLING_FINAL_SCORE = 0.45

export function candidateTier(candidate: UnifiedCandidate): SmartAnalysisTier {
  const category = candidate.full?.category
  if (category === 'top' || category === 'compelling' || category === 'precluded') {
    return category
  }
  const score = candidate.score ?? candidate.full?.final_score ?? 0
  if (score >= TOP_FINAL_SCORE) return 'top'
  if (score >= COMPELLING_FINAL_SCORE) return 'compelling'
  return 'precluded'
}

export type SmartAnalysisTierGroups = Record<SmartAnalysisTier, UnifiedCandidate[]>

/** Group candidates into tiers, preserving the incoming (already sorted) order within each tier. */
export function groupCandidatesByTier(candidates: UnifiedCandidate[]): SmartAnalysisTierGroups {
  const groups: SmartAnalysisTierGroups = { top: [], compelling: [], precluded: [] }
  for (const candidate of candidates) {
    groups[candidateTier(candidate)].push(candidate)
  }
  return groups
}

export type SmartAnalysisTierCounts = Record<SmartAnalysisTier, number>

export function tierCounts(groups: SmartAnalysisTierGroups): SmartAnalysisTierCounts {
  return {
    top: groups.top.length,
    compelling: groups.compelling.length,
    precluded: groups.precluded.length,
  }
}

/** One-line verdict, e.g. "3 Strong matches · 5 Good matches · 4 not recommended". */
export function tierDigestSegments(counts: SmartAnalysisTierCounts): string[] {
  const segments: string[] = []
  if (counts.top > 0) {
    segments.push(
      `${counts.top} ${counts.top === 1 ? 'Strong match' : 'Strong matches'}`,
    )
  }
  if (counts.compelling > 0) {
    segments.push(
      `${counts.compelling} ${counts.compelling === 1 ? 'Good match' : 'Good matches'}`,
    )
  }
  if (counts.precluded > 0) {
    segments.push(`${counts.precluded} not recommended`)
  }
  return segments
}
